<template>
  <div id="scroll-threshold-example" class="coupon-page">
    <div>
      <div class="mt-5" v-if="settings && settings.options">
        <v-row :class="$vuetify.breakpoint.smAndDown ? '' : 'mx-1'">
          <v-col cols="12">
            <v-container style="max-width:640px">
              <v-row class="px-3">
                <!-- Brand / Logo Header -->
                <v-col cols="12" md="12" class="pb-2">
                  <v-card class="brand-card text-center" flat>
                    <div class="brand-card-inner justify-center py-4">
                      <v-img
                        v-if="settings.options.logoDB"
                        :src="'/static/users/pictures/db/' + settings.options.logoDB"
                        :lazy-src="'/static/users/pictures/db/' + settings.options.logoDB"
                        max-width="220"
                        class="brand-logo mx-auto"
                      ></v-img>
                      <p v-else class="brand-name text-h5 mb-0">{{ settings.options.brand }}</p>
                    </div>
                  </v-card>
                  
                  <div v-if="settings.firstName || settings.lastName" class="greeting-block text-center mt-3">
                    <span class="eyebrow">{{ $t(page + '.Hi') }}</span>
                    <span class="guest-name">{{ settings.firstName ? settings.firstName : settings.lastName }}</span>
                  </div>
                </v-col>

                <!-- Loading State -->
                <v-col cols="12" v-if="loading">
                  <v-card class="text-center brand-card py-8" flat>
                    <v-progress-circular indeterminate size="64" width="3" color="#000000"></v-progress-circular>
                  </v-card>
                </v-col>

                <template v-else>
                  <!-- Coupon Image & Details Section -->
                  <v-col cols="12" class="pt-0">
                    
                    <!-- Coupon Image Card -->
                    <v-card class="coupon-card mb-4" flat v-if="settings.picture">
                      <v-img
                        v-if="settings.origin == 'ireview'"
                        :src="'https://ibookrestaurant.com/static/users/pictures/coupons/' + settings.picture"
                        :lazy-src="'https://ibookrestaurant.com/static/users/pictures/coupons/' + settings.picture"
                        max-height="300"
                        contain
                        class="mx-auto rounded"
                      ></v-img>
                      <v-img
                        v-else
                        :src="'https://ibookrestaurant.com/static/users/pictures/wa/' + settings.picture"
                        :lazy-src="'https://ibookrestaurant.com/static/users/pictures/wa/' + settings.picture"
                        max-height="300"
                        contain
                        class="mx-auto rounded"
                      ></v-img>
                    </v-card>

                    <!-- Details Box -->
                    <div class="coupon-section">
                      <div class="section-title text-h6 brand-title">
                        {{ settings.options.brand }}
                      </div>
                      
                      <div class="coupon-text text-body-1 mb-3">
                        {{ settings.couponText1 }}
                      </div>

                      <div class="coupon-rules">
                        <p class="rule-item mb-1">
                          <v-icon small color="#0B3B24" class="mr-1">mdi-calendar-clock</v-icon>
                          <span>{{ $t(page + '.Coupon is valid until') }} <strong>{{ settings.expiry }}</strong></span>
                        </p>

                        <p v-if="settings.validDoW && settings.validDoW.length !== 7" class="rule-item mb-1">
                          <v-icon small color="#0B3B24" class="mr-1">mdi-calendar-week</v-icon>
                          <span>
                            {{ $t(page + '.Coupon can be consumed only on') }}
                            <strong>
                              <span v-for="(d, di) in dows.filter(d => settings.validDoW.includes(d.code))" :key="di">
                                {{ d[locale].substring(0, 9).toUpperCase() }}{{ (di <= dows.filter(d => settings.validDoW.includes(d.code)).length - 2) ? ', ' : '' }}
                              </span>
                            </strong>
                          </span>
                        </p>

                        <p v-if="settings.maxCovers > 0" class="rule-item mb-0">
                          <v-icon small color="#0B3B24" class="mr-1">mdi-account-group</v-icon>
                          <span>{{ $t(page + '.and for not more than') }} <strong>{{ settings.maxCovers }} {{ $t(page + '.persons') }}</strong></span>
                        </p>
                      </div>
                    </div>

                    <!-- QR Code / Status Section -->
                    <div class="coupon-section text-center">
                      <!-- Active Coupon -->
                      <template v-if="!settings.spentDate && !settings.expired">
                        <div class="section-title text-subtitle-1 mb-3">
                          {{ $t(page + '.Show this QR Code to the waiter if requested') }}
                        </div>
                        <div class="qr-wrapper d-flex justify-center pa-4 mx-auto">
                          <qrcode-vue
                            :value="'https://ibookrestaurant.com/validateqr/?p=' + settings.couponId"
                            :size="200"
                            render-as="canvas"
                            level="L"
                          ></qrcode-vue>
                        </div>
                      </template>

                      <!-- Spent Coupon -->
                      <template v-else-if="settings.spentDate">
                        <div class="status-box status-spent pa-4">
                          <v-icon color="#D32F2F" large class="mb-2">mdi-check-circle-outline</v-icon>
                          <div class="text-h6 font-weight-bold red--text text--darken-2">
                            {{ $t(page + '.Sorry, Coupon already spent on') }} {{ settings.spentDate }}
                          </div>
                        </div>
                      </template>

                      <!-- Expired Coupon -->
                      <template v-else>
                        <div class="status-box status-expired pa-4">
                          <v-icon color="#D32F2F" large class="mb-2">mdi-clock-alert-outline</v-icon>
                          <div class="text-h6 font-weight-bold red--text text--darken-2">
                            {{ $t(page + '.Sorry, Coupon expired on') }} {{ settings.expiry }}
                          </div>
                        </div>
                      </template>
                    </div>

                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Scroll to Top Button -->
    <v-btn
      v-if="settings"
      v-scroll="onScroll"
      v-show="scrollToTop"
      fab
      dark
      small
      fixed
      bottom
      right
      color="#000000"
      @click="toTop"
    >
      <v-icon>keyboard_arrow_up</v-icon>
    </v-btn>

    <!-- Social Bottom Navigation -->
    <v-bottom-navigation
      color="orange"
      absolute
      hide-on-scroll
      scroll-target="#scroll-threshold-example"
      scroll-threshold="500"
      class="social-nav mt-6"
      style="position:unset"
      v-if="settings && settings.options"
    >
      <v-btn :href="settings.options.facebookURL" v-if="settings.options.facebookURL" text>
        <span>Facebook</span>
        <v-icon color="#3b5998">mdi-facebook</v-icon>
      </v-btn>
      <v-btn :href="settings.options.instagramURL" v-if="settings.options.instagramURL" text>
        <span>Instagram</span>
        <v-icon color="#C13584">mdi-instagram</v-icon>
      </v-btn>
      <v-btn :href="settings.options.youtubeURL" v-if="settings.options.youtubeURL" text>
        <span>YouTube</span>
        <v-icon color="#c4302b">mdi-youtube</v-icon>
      </v-btn>
      <v-btn :href="settings.options.linkedInURL" v-if="settings.options.linkedInURL" text>
        <span>LinkedIn</span>
        <v-icon color="#0077b5">mdi-linkedin</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-snackbar
      v-model="snackbar.active"
      top
      timeout="2000"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import QrcodeVue from 'qrcode.vue';

export default {
  components: {
    QrcodeVue,
  },
  data() {
    return {
      page: "Review",
      wHeight: window.innerHeight,
      wWidth: window.innerWidth,
      loading: false,
      origin: null,
      date: null,
      today: new Date().toISOString().substring(0, 10),
      locale: 'it',
      snackbar: { active: false, color: 'success', text: '' },
      scrollToTop: false,
      couponId: null,
      settings: null,
      QRCodeLink: null,
      dows: [
        { code: 1, it: 'Lunedì', en: 'Mondays' },
        { code: 2, it: 'Martedì', en: 'Tuesdays' },
        { code: 3, it: 'Mercoledì', en: 'Wednesdays' },
        { code: 4, it: 'Giovedì', en: 'Thursdays' },
        { code: 5, it: 'Venerdì', en: 'Fridays' },
        { code: 6, it: 'Sabato', en: 'Saturdays' },
        { code: 7, it: 'Domenica', en: 'Sundays' }
      ],
    };
  },
  created() {
    // Set theme to light mode as in Review.vue
    this.$vuetify.theme.dark = false;
    sessionStorage.removeItem('jwt');
    const la = window.navigator.userLanguage || window.navigator.language;
    this.locale = la.substring(0, 2);
    this.$store.dispatch("setLocale", this.locale);
    this.couponId = this.$route.query.p;
    this.origin = this.$route.path;
    var hostname = window.location.hostname;
    this.QRCodeLink = 'https://' + hostname + '/qrcodevalidate/?p=' + this.couponId;
    this.Authenticate(this.couponId);
  },
  computed: {
    ...mapGetters(["countries"]),
  },
  methods: {
    onScroll(e) {
      if (typeof window === 'undefined') return;
      const top = window.pageYOffset || e.target.scrollTop || 0;
      this.scrollToTop = top > 20;
    },
    toTop() {
      this.$vuetify.goTo(0);
    },
    async Authenticate(couponId) {
      this.loading = true;
      try {
        const payload = JSON.stringify({ "action": "AuthenticateCoupon", "payload": { "couponId": couponId } });
        const res = await this.$http.post('/review/review.php', payload);
        switch (res.data.errorid) {
          case '0':
            sessionStorage.setItem('jwt', res.data.jwt);
            this.settings = res.data.payloads;
            if (this.settings.options && this.settings.options.brand) {
              document.title = this.settings.options.brand + ' | Coupon';
            }
            break;
          case '0006':
            this.$router.push('/auth/login').catch(() => {});
            break;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Page Layout */
.coupon-page {
  background: #FFFFFF;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #16211C;
}

/* Brand Card */
.brand-card {
  background: #FFFFFF !important;
  border: 1px solid #E3E7E4;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.brand-card-inner {
  display: flex;
  align-items: center;
}

.brand-logo {
  border-radius: 6px;
}

.brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #0B3B24;
}

/* Greeting */
.greeting-block {
  margin: 6px 0;
}

.eyebrow {
  font-size: 15px;
  color: #16211C;
  margin-right: 6px;
}

.guest-name {
  font-size: 15px;
  font-weight: 700;
  color: #0B3B24;
}

/* Coupon Main Container & Sections */
.coupon-card {
  background: #FFFFFF !important;
  border: 1px solid #E3E7E4;
  border-radius: 8px !important;
  padding: 12px;
}

.coupon-section {
  background: #FFFFFF;
  border: 1px solid #E3E7E4;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.section-title {
  font-weight: 700;
  color: #0B3B24;
}

.brand-title {
  font-size: 20px;
  margin-bottom: 8px;
}

.coupon-text {
  color: #16211C;
  line-height: 1.5;
}

/* Rules list */
.coupon-rules {
  background: #F7F9F8;
  border-radius: 6px;
  padding: 12px 14px;
  margin-top: 12px;
}

.rule-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #4A5750;
}

.rule-item strong {
  color: #16211C;
}

/* QR Code Section */
.qr-wrapper {
  background: #FFFFFF;
  border: 1px dashed #B9C2BC;
  border-radius: 8px;
  width: fit-content;
}

/* Coupon Spent/Expired States */
.status-box {
  border-radius: 6px;
  background: #FFEBEE;
  border: 1px solid #FFCDD2;
}

/* Bottom Navigation */
.social-nav {
  background: transparent !important;
  border-top: 1px solid #E3E7E4;
  box-shadow: none !important;
}

.social-nav ::v-deep .v-btn {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 1px;
  color: #4A5750;
}

/* Responsive tweaks */
@media (max-width: 600px) {
  .coupon-section {
    padding: 16px;
    margin-top: 12px;
  }
}
</style>